import axiosInstance from "../axiosInstance";
import { distinctValuesService } from "./distinct-values.service";

/**
 * Returns list/detail reads, plus the Pipe17 returns backfill trigger.
 *
 * `/return` is control-tower's shared `api/return` service — one row per
 * (client_id, oms_provider, external_id), written by BOTH providers' returns
 * pipelines (see control-tower/src/subscriber/oms-sync-subscriber.js's
 * createReturn). Nothing here names a provider — `syncReturns` looks up the
 * client's own OMS providers before dispatching — so a
 * Shopify return renders through the same page.
 */
export const returnService = {
  /**
   * Newest first, matching every other list in the portal. `$populate` pulls
   * the full product record for each line item — the model declares
   * `line_items.product` as a real ref, and the service whitelists $populate
   * for exactly this.
   */
  fetch: async (client, query, signal) => {
    try {
      let apiQuery = `/return?$sort[createdAt]=-1`;
      if (client) {
        apiQuery += `&client_id=${client}`;
      }
      if (query && query.length) apiQuery += `&${query}`;
      const response = await axiosInstance.get(apiQuery, signal);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  fetchById: async (id) => {
    try {
      const response = await axiosInstance.get(
        `/return/${id}?$populate[]=line_items.product`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Distinct values for a filter dropdown, via the shared `/distinct-values`
   * endpoint (control-tower whitelists exactly status / provider_status /
   * oms_provider / order_name on the `return` service — see its ALLOWED_FIELDS).
   *
   * Its own call rather than being derived from the current page, so the
   * dropdown offers every value the client has ever had — not just the ones
   * that happen to be visible on page 1.
   */
  fetchDistinct: async (field, searchKey = "", client_id, limit = 15, skip = 0) =>
    distinctValuesService.fetchColumnValues(
      "return",
      field,
      searchKey,
      client_id,
      limit,
      skip
    ),

  fetchStatuses: (searchKey = "", client_id, limit = 15, skip = 0) =>
    returnService.fetchDistinct("status", searchKey, client_id, limit, skip),

  fetchProviders: (searchKey = "", client_id, limit = 15, skip = 0) =>
    returnService.fetchDistinct("oms_provider", searchKey, client_id, limit, skip),

  fetchOrderNames: (searchKey = "", client_id, limit = 15, skip = 0) =>
    returnService.fetchDistinct("order_name", searchKey, client_id, limit, skip),

  /**
   * Sales channels. Stored on the return (resolved from its order — see
   * control-tower's return-channel.js), so this is an ordinary distinct-values
   * lookup like every other dropdown, and the filter it feeds is a plain
   * `channel[$in][]=` match.
   */
  fetchChannels: (searchKey = "", client_id, limit = 15, skip = 0) =>
    returnService.fetchDistinct("channel", searchKey, client_id, limit, skip),

  /**
   * The return id shown in the RETURN column — `name`, which is the provider's
   * own reference (Pipe17's extReturnId, falling back to its raw returnId).
   *
   * Fed from /distinct-values like every other dropdown rather than left as a
   * bare text box, because the list query can only express `name[$in][]=` — the
   * `return` service whitelists $populate only, so $regex on the list endpoint
   * is rejected by Feathers' default operator set. The dropdown's search box
   * does the narrowing server-side here instead, which is also what makes this
   * usable on a high-cardinality field.
   */
  fetchNames: (searchKey = "", client_id, limit = 15, skip = 0) =>
    returnService.fetchDistinct("name", searchKey, client_id, limit, skip),

  /**
   * Kick off the returns backfill for a client, on every OMS provider of theirs
   * that can page a return history.
   *
   * Which control-tower service to PATCH depends on the provider: each of
   * manage-pipe17 and manage-acenda answers the same
   * `{ action: "sync-returns", since?, until? }` contract for its own
   * provider, and neither knows about the other's oms-config. So the client's
   * oms-configs are read first (populated with the provider doc) and the call
   * fans out to the matching manage-* per provider. A client on a provider with
   * no returns list (Shopify, RL_OMS) is told so up front instead of getting a
   * "no oms-config" 400 from a service that was never theirs.
   *
   * Fire-and-forget all the way down: control-tower dispatches to rl-oms-be,
   * which asks the provider to page its return history back as inbound
   * references. This resolves as soon as the paging job is QUEUED — the returns
   * themselves land asynchronously, so the caller must re-fetch the list rather
   * than expecting the response to contain them.
   */
  syncReturns: async (client_id, range = {}) => {
    const omsRes = await axiosInstance.get(
      `/oms-config?client_id=${client_id}&$limit=50&$populate=provider`
    );
    const configs = (omsRes.data && omsRes.data.data) || [];
    const targets = [
      ...new Set(
        configs
          .map((c) => RETURN_SYNC_SERVICES[c?.provider?.name])
          .filter(Boolean)
      ),
    ];
    if (!targets.length) {
      const names = [...new Set(configs.map((c) => c?.provider?.name).filter(Boolean))];
      throw new Error(
        names.length
          ? `Returns sync is not available for ${names.join(", ")} — only ${Object.keys(RETURN_SYNC_SERVICES).join(" and ")} expose a return history to import`
          : "This client has no OMS provider configured yet — onboard the client first"
      );
    }
    const body = { client_id, action: "sync-returns" };
    if (range.since) body.since = range.since;
    if (range.until) body.until = range.until;
    const results = [];
    for (const service of targets) {
      const response = await axiosInstance.patch(`/${service}/${client_id}`, body);
      results.push(response.data);
    }
    return results;
  },
};

/**
 * OMS providers that expose a bulk returns list, and the control-tower service
 * whose `sync-returns` action triggers it. Extend this when a provider grows a
 * returns backfill; the page and modal stay provider-neutral.
 */
const RETURN_SYNC_SERVICES = {
  Pipe17: "manage-pipe17",
  Acenda: "manage-acenda",
};
